import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Output,
  Input,
  ViewChild,
  ElementRef,
  inject,
  AfterViewInit,
  NgZone,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  computed,
  signal,
  HostListener,
} from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { HeaderComponent, NebulaNavigationService } from 'nebula-ui-kit';
import { LucideAngularModule } from 'lucide-angular';
import {
  TranslationService,
  TranslatePipe,
  AuthService,
  PerfilService,
  PerfilData,
  PerfilModalLauncher,
  SystemSyncTopbarDateService,
} from '@nebula-shared/data-access';
import { Subject } from 'rxjs';
import {
  GlobalMenuSearchService,
  MenuSearchOption,
} from '../../../../data-access/src/lib/global-menu-search.service';

@Component({
  selector: 'app-topbar-wrapper',
  templateUrl: './topbar-wrapper.component.html',
  styleUrls: ['./topbar-wrapper.component.scss'],
  standalone: true,
  imports: [CommonModule, MatMenuModule, HeaderComponent, LucideAngularModule, TranslatePipe],
})
export class TopbarWrapperComponent implements AfterViewInit, OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly navService = inject(NebulaNavigationService);
  private readonly ngZone = inject(NgZone);
  private readonly systemDateService = inject(SystemSyncTopbarDateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly menuSearch = inject(GlobalMenuSearchService);
  private readonly translation = inject(TranslationService);
  private readonly elementRef = inject(ElementRef);
  private readonly perfilService = inject(PerfilService);
  private readonly perfilModalLauncher = inject(PerfilModalLauncher);
  private readonly authService = inject(AuthService);
  private readonly destroy$ = new Subject<void>();

  readonly userFullName = signal<string>('');
  readonly userRoles = signal<string>('');

  readonly languages = this.translation.availableLanguages;
  readonly activeLang = signal<string>(this.translation.currentLang);
  readonly langMenuOpen = signal<boolean>(false);
  readonly langMenuTop = signal<number>(0);
  readonly langMenuRight = signal<number>(8);

  readonly activeFlag = computed(() => {
    const code = this.activeLang();
    const lang = this.languages().find((l) => l.code === code);
    return lang?.flagEmoji || code.toUpperCase();
  });

  toggleLangMenu(): void {
    const willOpen = !this.langMenuOpen();
    if (willOpen) {
      const host = this.elementRef.nativeElement as HTMLElement;
      const btn = host.querySelector('.lang-btn') as HTMLElement | null;
      if (btn && typeof window !== 'undefined') {
        const r = btn.getBoundingClientRect();
        this.langMenuTop.set(Math.round(r.bottom + 6));
        this.langMenuRight.set(Math.max(8, Math.round(window.innerWidth - r.right)));
      }
    }
    this.langMenuOpen.set(willOpen);
  }

  async selectLanguage(code: string): Promise<void> {
    this.langMenuOpen.set(false);
    await this.translation.setLanguage(code);
    this.activeLang.set(code);
  }

  profileItemLabel(item: { id: number; action: string }): string {
    const key = item.id === 3 ? 'NAV.SIGN_OUT' : item.id === 1 ? 'TITLE.SELECT_PRODUCTS' : '';
    return key ? this.translation.translate(key) : item.action;
  }

  readonly searchResults = signal<MenuSearchOption[]>([]);
  readonly showSearchResults = signal<boolean>(false);
  readonly searchToast = signal<string | null>(null);
  private searchToastTimeout: ReturnType<typeof setTimeout> | null = null;

  @Input() userName = '';
  @Input() isDarkMode = false;
  @Input() logoUrl = '';
  @Input() itemsProfileMenu: Array<{ id: number; action: string }> = [];

  @Input() showSearch = true;
  @Input() showNotifications = true;
  @Input() showRefresh = true;
  @Input() showSystemDate = true;
  @Input() showProducts = true;

  @Output() darkModeToggle = new EventEmitter<void>();
  @Output() profileClick = new EventEmitter<void>();
  @Output() logout = new EventEmitter<void>();

  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  @ViewChild('triggerButton') triggerButton!: ElementRef<HTMLButtonElement>;

  systemDate = computed(() => this.systemDateService.fechaSistemaFormatted());
  isLoadingSystemDate = computed(() => this.systemDateService.isLoading());

  private readonly MENU_WIDTH = 170;
  private readonly MENU_OFFSET = 8;
  private readonly OVERLAY_RIGHT_MARGIN = 26.67;
  private isMenuOpening = false;

  ngOnInit(): void {
    // El modulo lo declara la ruta y la fecha la mantiene al dia
    // `SystemSyncTopbarDateService`, que ya escucha la navegacion. Aqui solo se
    // pide la primera, por si se entra sin haber navegado todavia.
    if (!this.systemDateService.fechaSistema()) {
      this.systemDateService.loadSystemDate();
    }

    this.menuSearch.loadOptions();
    this.cargarPerfilMenu();
  }

  private cargarPerfilMenu(): void {
    const roles = this.authService.getUserRoles() ?? [];
    this.userRoles.set(
      roles
        .map((r: any) => {
          const key = r?.name || r?.authority || '';
          const traducido = this.translation.translate(key);
          return traducido && traducido !== key ? traducido : r?.description || key;
        })
        .filter((label: string) => !!label)
        .join(', '),
    );
    this.perfilService.getMiPerfil().subscribe({
      next: (perfil) => this.setFullName(perfil),
      error: () => this.userFullName.set(''),
    });
  }

  private setFullName(perfil: PerfilData): void {
    this.userFullName.set(`${perfil?.nombres ?? ''} ${perfil?.apellidos ?? ''}`.trim());
  }

  private abrirPerfil(): void {
    this.perfilModalLauncher.open().subscribe((result) => {
      if (result) {
        this.setFullName(result);
      }
    });
  }

  onGlobalSearch(query: string): void {
    const q = (query || '').trim();
    if (q.length < 2) {
      this.searchResults.set([]);
      this.showSearchResults.set(false);
      return;
    }
    this.searchResults.set(this.menuSearch.search(q));
    this.showSearchResults.set(true);
    this.cdr.markForCheck();
  }

  onSelectSearchResult(option: MenuSearchOption): void {
    const result = this.menuSearch.openOption(option);
    if (result === 'limit') {
      this.showSearchToast(this.translation.translate('LAYOUT.MAX_TABS', { 0: 8 }));
      return;
    }
    this.closeSearchResults();
  }

  closeSearchResults(): void {
    this.showSearchResults.set(false);
    this.searchResults.set([]);
  }

  private showSearchToast(msg: string): void {
    this.searchToast.set(msg);
    if (this.searchToastTimeout) clearTimeout(this.searchToastTimeout);
    this.searchToastTimeout = setTimeout(() => {
      this.searchToast.set(null);
      this.cdr.markForCheck();
    }, 2500);
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown', ['$event'])
  onSearchKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K')) {
      event.preventDefault();
      const input = this.elementRef.nativeElement.querySelector(
        'lib-header input',
      ) as HTMLInputElement | null;
      input?.focus();
    }
    if (event.key === 'Escape' && this.showSearchResults()) {
      event.preventDefault();
      this.closeSearchResults();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const outside = !this.elementRef.nativeElement.contains(event.target);
    if (outside && this.showSearchResults()) {
      this.closeSearchResults();
    }
    if (outside && this.langMenuOpen()) {
      this.langMenuOpen.set(false);
    }
  }

  ngAfterViewInit(): void {
    if (this.triggerButton?.nativeElement) {
      this.ngZone.runOutsideAngular(() => {
        const btn = this.triggerButton.nativeElement;
        btn.style.opacity = '0';
        btn.style.pointerEvents = 'none';
      });
    }
    this.relocateLangSelector();
  }

  private relocateLangSelector(attempt = 0): void {
    setTimeout(() => {
      const host = this.elementRef.nativeElement as HTMLElement;
      const selector = host.querySelector('.lang-selector') as HTMLElement | null;
      const profile =
        (host.querySelector('lib-header .user-profile') as HTMLElement | null) ||
        (document.querySelector('lib-header .user-profile') as HTMLElement | null);

      if (selector && profile?.parentElement) {
        if (selector.nextElementSibling !== profile) {
          profile.parentElement.insertBefore(selector, profile);
        }
        return;
      }
      if (attempt < 20) {
        this.relocateLangSelector(attempt + 1);
      }
    }, 50);
  }

  showMenu(headerRef?: any): void {
    if (this.isMenuOpening) return;
    this.isMenuOpening = true;

    Promise.resolve().then(() => this.profileClick.emit());

    let targetElement: HTMLElement | null = null;
    if (headerRef?.elementRef?.nativeElement) {
      targetElement = headerRef.elementRef.nativeElement.querySelector('.user-profile');
    }
    if (!targetElement) {
      targetElement = document.querySelector('.user-profile');
    }

    if (!targetElement || !this.triggerButton?.nativeElement || !this.menuTrigger) {
      this.isMenuOpening = false;
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    const button = this.triggerButton.nativeElement;

    this.ngZone.runOutsideAngular(() => {
      button.removeAttribute('style');
      button.style.position = 'fixed';
      button.style.top = `${rect.bottom + this.MENU_OFFSET}px`;
      button.style.left = `${rect.right - this.MENU_WIDTH}px`;
      button.style.width = `${this.MENU_WIDTH}px`;
      button.style.height = '1px';
      button.style.opacity = '0';
      button.style.pointerEvents = 'none';
      button.style.zIndex = '-1';
    });

    this.menuTrigger.updatePosition();

    setTimeout(() => {
      this.ngZone.run(() => {
        if (this.menuTrigger && !this.menuTrigger.menuOpen) {
          this.menuTrigger.openMenu();
        }
        this.isMenuOpening = false;
      });
    }, 0);
  }

  closeMenu(): void {
    if (this.menuTrigger?.menuOpen) {
      this.menuTrigger.closeMenu();
    }
  }

  onDarkModeToggle(): void {
    this.darkModeToggle.emit();
  }

  onProfileAction(item: { id: number; action: string }): void {
    this.closeMenu();

    switch (item.id) {
      case 2:
        this.abrirPerfil();
        break;
      case 3:
        this.onLogout();
        break;
      default:
        break;
    }
  }

  onSelectProducts(): void {
    const keysToRemove = [
      'nebula:activeRoute:accounting',
      'nebula:activeRoute:budget',
      'nebula:activeRoute:tesorería',
      'nebula:activeRoute:vault',
      'nebula:activeRoute:invoicing',
      'nebula_ui_nav_v1',
      'selectedRoute',
      'selectedProductName',
      'nebula:navigation:state',
    ];

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });

    this.navService.clearTabs();
    this.navService.setContext('clean');
    this.router.navigate(['/layout/products']);
  }

  onLogout(): void {
    this.logout.emit();
  }

  productsClick(): void {
    this.onSelectProducts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
